import React, { useContext } from 'react';
import { DashboardContext } from '../../../context/DashboardContext';
import { IndianRupee, Tag, Receipt, BadgePercent } from 'lucide-react';
import { formatCurrency } from '../../../lib/utils';

export default function FinancialSnapshot() {
  const { dashboardData } = useContext(DashboardContext);
  const { loading, financialSnapshot } = dashboardData || {};

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-6"></div>
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  const {
    totalRevenue = 0,
    totalDiscount = 0,
    totalNcAmount = 0,
    totalTax = 0,
    grossSales = 0,
  } = financialSnapshot || {};

  // Deductions total
  const totalDeductions = totalDiscount + totalNcAmount;

  const metrics = [
    {
      key: 'revenue',
      label: 'Net Revenue',
      value: totalRevenue,
      icon: IndianRupee,
      accent: 'emerald',
      bgGradient: 'from-emerald-50 to-emerald-100/50',
      iconBg: 'bg-emerald-500',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
      description: 'After all deductions',
    },
    {
      key: 'tax',
      label: 'Tax Collected',
      value: totalTax,
      icon: Receipt,
      accent: 'sky',
      bgGradient: 'from-sky-50 to-sky-100/50',
      iconBg: 'bg-sky-500',
      textColor: 'text-sky-700',
      borderColor: 'border-sky-200',
      description: 'GST (CGST + SGST)',
    },
    {
      key: 'discount',
      label: 'Discounts',
      value: totalDiscount,
      icon: BadgePercent,
      accent: 'violet',
      bgGradient: 'from-violet-50 to-violet-100/50',
      iconBg: 'bg-violet-500',
      textColor: 'text-violet-700',
      borderColor: 'border-violet-200',
      description: 'Promos & manual',
    },
    {
      key: 'nc',
      label: 'NC Amount',
      value: totalNcAmount,
      icon: Tag,
      accent: 'amber',
      bgGradient: 'from-amber-50 to-amber-100/50',
      iconBg: 'bg-amber-500',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
      description: 'Non-chargeable',
    },
  ];

  // Percentage bar segments
  const segments = [
    { key: 'revenue', label: 'Revenue', value: totalRevenue, color: 'bg-emerald-500' },
    { key: 'tax', label: 'Tax', value: totalTax, color: 'bg-sky-400' },
    { key: 'discount', label: 'Discount', value: totalDiscount, color: 'bg-violet-400' },
    { key: 'nc', label: 'NC', value: totalNcAmount, color: 'bg-amber-400' },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-5 pb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Financial Overview</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Gross Sales: {formatCurrency(grossSales)}
          </p>
        </div>
        {totalDeductions > 0 && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Deductions</p>
            <p className="text-sm font-bold text-rose-600">{formatCurrency(totalDeductions)}</p>
          </div>
        )}
      </div>

      {/* Composition Bar */}
      {grossSales > 0 && (
        <div className="px-6 pb-3">
          <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
            {segments.map(seg => {
              const pct = grossSales > 0 ? (seg.value / grossSales) * 100 : 0;
              if (pct < 0.5) return null;
              return (
                <div
                  key={seg.key}
                  className={`${seg.color} transition-all duration-500`}
                  style={{ width: `${pct}%` }}
                  title={`${seg.label}: ${formatCurrency(seg.value)} (${pct.toFixed(1)}%)`}
                />
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-3 mt-2">
            {segments.map(seg => {
              const pct = grossSales > 0 ? (seg.value / grossSales) * 100 : 0;
              if (pct < 0.5) return null;
              return (
                <div key={seg.key} className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${seg.color}`}></span>
                  <span className="text-[10px] text-slate-500">{seg.label} {pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Metric Cards */}
      <div className="px-5 pb-5">
        <div className="grid grid-cols-2 gap-3">
          {metrics.map(m => (
            <div
              key={m.key}
              className={`relative bg-gradient-to-br ${m.bgGradient} rounded-xl border ${m.borderColor} p-4 transition-all hover:shadow-sm`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`${m.iconBg} p-1.5 rounded-lg text-white`}>
                  <m.icon size={14} strokeWidth={2.5} />
                </div>
                <p className={`text-[10px] font-medium ${m.textColor} opacity-70`}>{m.description}</p>
              </div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{m.label}</p>
              <p className={`text-xl font-bold ${m.textColor} mt-0.5 leading-tight`}>
                {formatCurrency(m.value)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
